import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const sections = [
  {
    title: 'Getting Started',
    content: `Create a project to organize your documents. Upload PDF files to a project, then analyze them to detect PII (Personally Identifiable Information). Once analysis is complete, you can process the document to automatically redact detected PII.`,
  },
  {
    title: 'Document Processing',
    content: `Documents are processed page by page. Text-based pages cost 1 credit each, while image-based pages (scanned documents) cost 5 credits each due to the additional OCR processing required. The system automatically detects which pages are text-based and which require OCR.`,
  },
  {
    title: 'Credits System',
    content: `Credits are used to process documents. You receive 10 free credits when you sign up. Additional credits can be purchased in various quantities — buying in bulk unlocks better pricing tiers. Credits never expire and are allocated instantly after purchase.`,
  },
  {
    title: 'Downloading Results',
    content: `After processing, you can download the redacted PDF with all detected PII items masked. The original document is preserved — redactions are applied to a new copy. You can also view a detailed report of all PII items found in each document.`,
  },
  {
    title: 'Manual Redactions',
    content: `In addition to automatic PII detection, you can manually add redaction regions to any page. Use the redaction editor to draw boxes over sensitive areas that the automated system may have missed. Manual redactions are combined with automatic ones in the final output.`,
  },
];

function AccordionItem({ title, content, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-200 dark:border-dark-border last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-slate-800/50 transition"
      >
        <span className="text-sm font-medium text-gray-900 dark:text-white">{title}</span>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{content}</p>
        </div>
      )}
    </div>
  );
}

export default function HowItWorksSection() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
      <div className="p-6 border-b border-gray-200 dark:border-dark-border">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <HelpCircle className="w-5 h-5" />
          How It Works
        </h2>
      </div>

      {sections.map((section, index) => (
        <AccordionItem
          key={index}
          title={section.title}
          content={section.content}
          isOpen={openIndex === index}
          onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
        />
      ))}
    </div>
  );
}
